import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { ButtonWrap } from "./BackToLandingButtonStyles";
import { useTranslation } from "react-i18next"; // 👈 import

const MOBILE_BP = 768;

export default function BackToLandingButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(); // 👈 iau t()

  // nu afișăm pe landing
  const isLanding =
    location.pathname === "/" || location.pathname === "/index.html";

  // detect mobil (ca la LanguageSwitcher)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP}px)`);
    const onChange = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    onChange();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // vizibilitate (mobil: auto-hide; desktop: mereu vizibil)
  const [visible, setVisible] = useState(false);
  const lastYRef = useRef(0);
  const hideTimerRef = useRef(null);

  const kick = useMemo(
    () => () => {
      setVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setVisible(false), 4000);
    },
    []
  );

  // Inițializare per pagină:
  useEffect(() => {
    if (isLanding) {
      setVisible(false);
      return;
    }
    if (isMobile) {
      const t = setTimeout(kick, 300); // mobil: apare după 300ms, apoi auto-hide
      return () => clearTimeout(t);
    } else {
      setVisible(true); // desktop: mereu vizibil
    }
  }, [isLanding, isMobile, kick]);

  // mobil: ascunde la scroll în jos, arată la scroll în sus
  useEffect(() => {
    if (!isMobile || isLanding) return;
    const onScroll = () => {
      const y = window.scrollY || 0;
      const goingDown = y > (lastYRef.current || 0);
      setVisible(!goingDown);
      lastYRef.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile, isLanding]);

  // mobil: re-afișează la interacțiune și repornește auto-hide
  useEffect(() => {
    if (!isMobile || isLanding) return;
    window.addEventListener("touchstart", kick, { passive: true });
    window.addEventListener("mousemove", kick);
    return () => {
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("mousemove", kick);
    };
  }, [isMobile, isLanding, kick]);

  // cleanup timer
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  if (isLanding) return null;

  const label = t("landing.backButtonLabel"); // 👈 o singură cheie pt text

  return createPortal(
    <ButtonWrap
      $visible={isMobile ? visible : true} // desktop: forțăm mereu vizibil
      onClick={() => navigate("/")}
      title={t("landing.backButtonTitle")}
      aria-label={t("landing.backButtonAria")}
      id="back-to-landing-btn"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
      <span>{label}</span>
    </ButtonWrap>,
    document.body
  );
}
